<template>
  <div class="p-4">
    <b-row>
      <b-col md="12">
        <h3 class="fulfillment-form-header">
          Pickup
          <i class="ic-tooltip" id="help-pickup"></i>
          <b-popover target="help-pickup"
              triggers="hover"
              content="Set-up your online store’s customer pick-up options for same-day and/or advanced orders.">
          </b-popover>
        </h3>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col lg="2" md="4">
        <b-form-group label="Pickup Settings" label-class="fulfillment-form-label"></b-form-group>
      </b-col>
      <b-col lg="10" md="8">
        <div class="bg-white rounded p-4 form-container">
          <b-form-group>
            <div class="d-flex align-items-center">
              <span class="mr-2">Pickup Now</span>
              <switches
                v-model="form.pickup_now.enable"
                theme="custom"
                color="primary"
                type-bold="true"
                style="top: 12px;"
                :emit-on-mount="false"
                @input="$root.$emit('changesDetected')"
                :disabled="isFulfillmentNotSet && !form.scheduled_pickup.enable && form.pickup_now.enable">
              </switches>
              <span class="ml-2">
                <small class="text-muted">{{ `${form.pickup_now.enable ? 'Enabled' : 'Disabled'}` }}</small>
              </span>
            </div>
            <b-form-invalid-feedback :force-show="isInvalid('pickup_now.enable')">
              {{ errorMessage('pickup_now.enable') }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group>
            <b-form-checkbox
              v-model="form.pickup_now.same_as_delivery"
              class="mb-3"
              :disabled="!form.pickup_now.enable || isSameDeliveryDisabled('pickup_now')"
              @change="getDeliverySetup('pickup_now')">
              <span class="offset-top">Same as delivery setup</span>
            </b-form-checkbox>
          </b-form-group>
          <BrandBranchesComponent
            :enabled="form.pickup_now.enable"
            @setBrandsBranches="handleSetBrandsBranches"
            :selectedValue="pickup_now_branches"
            origin="pickup_now"
            error-key="pickup_now.branch_uuids"
            :is-altered="is_altered.pickup_now"
          />
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-4">
      <b-col lg="2" md="4">
        <b-form-group label="Pre-Order Schedule Settings" label-class="fulfillment-form-label"></b-form-group>
      </b-col>
      <b-col lg="10" md="8">
        <div class="bg-white rounded p-4 form-container">
          <b-form-group>
            <div class="d-flex align-items-center">
              <span class="mr-2">Scheduled</span>
              <switches
                v-model="form.scheduled_pickup.enable"
                theme="custom"
                color="primary"
                type-bold="true"
                style="top: 12px;"
                :emit-on-mount="false"
                @input="$root.$emit('changesDetected')"
                :disabled="isFulfillmentNotSet && !form.pickup_now.enable && form.scheduled_pickup.enable">
              </switches>
              <span class="ml-2">
                <small class="text-muted">{{ `${form.scheduled_pickup.enable ? 'Enabled' : 'Disabled'}` }}</small>
              </span>
            </div>
            <b-form-invalid-feedback :force-show="isInvalid('scheduled_pickup.enable')">
              {{ errorMessage('scheduled_pickup.enable') }}
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group>
            <b-form-checkbox
              v-model="form.scheduled_pickup.same_as_delivery"
              class="mb-3"
              :disabled="!form.scheduled_pickup.enable || isSameDeliveryDisabled('scheduled_pickup')"
              @change="getDeliverySetup('scheduled_pickup')">
              <span class="offset-top">Same as delivery setup</span>
            </b-form-checkbox>
          </b-form-group>
          <LeadTimeComponent
            :enabled="form.scheduled_pickup.enable"
            @setLeadTimeValues="handleSetLeadTimeValues"
            origin="scheduled_pickup"
            :selectedValue="leadTimeValues" />
          <BrandBranchesComponent
            :enabled="form.scheduled_pickup.enable"
            @setBrandsBranches="handleSetBrandsBranches"
            :selectedValue="scheduled_pickup_branches"
            origin="scheduled_pickup"
            error-key="scheduled_pickup.branch_uuids"
            :is-altered="is_altered.scheduled_pickup"
          />
        </div>
      </b-col>
    </b-row>
    <b-row class="mt-4">
        <b-col lg="2" md="4">
        <b-form-group label="Pre-Ordering Settings" label-class="fulfillment-form-label"></b-form-group>
      </b-col>
      <b-col lg="10" md="8">
        <div class="bg-white rounded p-4 form-container">
          <b-form-group class="mb-0">
            <ScheduleSelectorComponent
              :items="scheduled_pickup_branches"
              origin="Pickup"
              :enabled="form.scheduled_pickup.enable"
            />
          </b-form-group>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Switches from "vue-switches";
import BrandBranchesComponent from "@/components/fulfillment/BrandBranchesComponent";
import LeadTimeComponent from "@/components/fulfillment/LeadTimeComponent";
import ScheduleSelectorComponent from "@/components/fulfillment/ScheduleSelectorComponent";

function initForm() {
  return {
    pickup_now: {
      enable: false,
      same_as_delivery: false,
      branch_uuids: []
    },
    scheduled_pickup: {
      enable: false,
      same_as_delivery: false,
      branch_uuids: [],
      minimum_lead_time: null,
      minimum: null,
      duration: null,
      duration_lead_time: null
    },
  }
}

export default {
  name: "FulfillmentPickupIndex",
  components: { Switches, BrandBranchesComponent, LeadTimeComponent, ScheduleSelectorComponent },
  data() {
    return {
      form: initForm(),
      pickup_now_branches: [],
      scheduled_pickup_branches: [],
      is_altered: {
        pickup_now: false,
        scheduled_pickup: false
      }
    }
  },
  computed: {
    ...mapGetters({
      errors: 'orderFulfillment/errors',
      pickup_tab_settings: 'orderFulfillment/pickup_tab_settings',
      delivery_tab_settings: 'orderFulfillment/delivery_tab_settings'
    }),
    leadTimeValues: {
      get() {
        const scheduled_pickup = this.form.scheduled_pickup;
        return {
          minimum: scheduled_pickup.minimum,
          minimum_lead_time: scheduled_pickup.minimum_lead_time,
          duration: scheduled_pickup.duration,
          duration_lead_time: scheduled_pickup.duration_lead_time
        }
      },
      set(lead_time_values) {
        return lead_time_values;
      }
    },
    isFulfillmentNotSet: {
      get() {
        const delivery_tab_settings = this.delivery_tab_settings;
        const pickup_tab_settings = this.pickup_tab_settings;

        return (!delivery_tab_settings || delivery_tab_settings.length === 0 || Object.keys(delivery_tab_settings).length === 0)
          && (!pickup_tab_settings || pickup_tab_settings.length === 0 || Object.keys(pickup_tab_settings).length === 0)
          || delivery_tab_settings && !delivery_tab_settings.delivery_settings.is_enabled && !delivery_tab_settings.pre_order_schedule_settings.is_enabled
            ? true
            : false;

      },
      set(settings) {
        return settings;
      }
    }
  },
  mounted() {
    this.$root.$on('handleFulfillmentSave', async (tabOrigin, store = null) => {
      if (tabOrigin === 'FulfillmentPickup') {
        await this.handleSavePickup();
        if (store) this.$router.push({ name: `${tabOrigin}PreOrdering`, params: { brand_uuid: store.brand_uuid, branch_uuid: store.branch_uuid } });
      }
    });
    this.$root.$on('saveFulfillmentChangesCancelled', (tabOrigin) => {
      if (tabOrigin === 'FulfillmentPickup') {
        this.setPickupSettings();
      }
    });
  },
  beforeDestroy() {
    this.$root.$off('handleFulfillmentSave');
    this.$root.$off('saveFulfillmentChangesCancelled');
  },
  async created() {
    await this.getPickupSettings();
    await this.getDeliverySettings();
    this.setPickupSettings();
  },
  methods: {
    ...mapActions({
      getPickupSettings: 'orderFulfillment/getPickupSettings',
      getPickupSettings: 'orderFulfillment/getPickupSettings',
      savePickupSettings: 'orderFulfillment/savePickupSettings',
      getDeliverySettings: 'orderFulfillment/getDeliverySettings',
    }),
    getDeliverySetup(type) { // set values same as delivery settings
      this.$nextTick(() => {
        if (this.form[type]['same_as_delivery']) {
          const delivery_tab_settings = this.delivery_tab_settings;
          if (delivery_tab_settings && type === 'pickup_now') {
            this.form.pickup_now.branch_uuids = delivery_tab_settings.delivery_settings.enabled_brands.map(brand => {
              return brand.branches.map(branch => branch.uuid)
            }).flat();

            this.pickup_now_branches = delivery_tab_settings.delivery_settings.enabled_brands;
          } else if (delivery_tab_settings && type === 'scheduled_pickup') {
            this.form.scheduled_pickup.branch_uuids = delivery_tab_settings.pre_order_schedule_settings.enabled_brands.map(brand => {
              return brand.branches.map(branch => branch.uuid)
            }).flat();
            this.form.scheduled_pickup.minimum_lead_time = delivery_tab_settings.pre_order_schedule_settings.lead_time.minimum;
            this.form.scheduled_pickup.minimum = delivery_tab_settings.pre_order_schedule_settings.lead_time.minimum_by;
            this.form.scheduled_pickup.duration = delivery_tab_settings.pre_order_schedule_settings.lead_time.duration_by;
            this.form.scheduled_pickup.duration_lead_time = delivery_tab_settings.pre_order_schedule_settings.lead_time.duration;

            this.scheduled_pickup_branches = delivery_tab_settings.pre_order_schedule_settings.enabled_brands;
          }
        }
        this.$root.$emit('changesDetected');
      });
    },
    async handleSavePickup() {
      await this.savePickupSettings(this.form);
      if (!this.errors) {
        this.$root.$emit('fulfillmentSavedSuccessful');
        this.getPickupSettings();
      }
    },
    handleSetBrandsBranches(param) {
      this[`${param.from}_branches`] = param.data; // store temporarily
      // store in form object
      this.form[`${param.from}`].branch_uuids = param.data.map(brand => {
        return brand.branches.map(branch => branch.uuid);
      }).flat();
      this.is_altered[`${param.from}`] = true;
      this.$root.$emit("changesDetected");
    },
    handleSetLeadTimeValues(leadTimeValues) {
      Object.keys(leadTimeValues).forEach(key => {
        this.form.scheduled_pickup[key] = leadTimeValues[key];
      });
      const lead_time_values = this.delivery_tab_settings
        ? this.delivery_tab_settings.pre_order_schedule_settings.lead_time
        : null;
      
      const pickup_settings_keys = {
        minimum: 'minimum_lead_time',
        minimum_by: 'minimum',
        duration: 'duration_lead_time',
        duration_by: 'duration'
      };
      
      if (lead_time_values) {
        Object.keys(pickup_settings_keys).forEach(key => {
          if (this.form.scheduled_pickup[pickup_settings_keys[key]] !== lead_time_values[key]) this.is_altered['scheduled_pickup'] = true;
        });
      }
    },
    isInvalid(key) {
      return this.errors && this.errors[key]
        ? false
        : null;
    },
    errorMessage(key) {
      return this.errors && this.errors[key]
        ? this.errors[key].split('.').join(' ')
        : null;
    },
    setPickupSettings() {
      const pickup_tab_settings = this.pickup_tab_settings;

      if (pickup_tab_settings && (pickup_tab_settings.length > 0 || Object.keys(pickup_tab_settings).length > 0)) {
        this.form = {
          pickup_now: {
            enable: pickup_tab_settings.pickup_settings.is_enabled == 1 ? true : false,
            same_as_delivery: pickup_tab_settings.pickup_settings.same_as_delivery,
            branch_uuids: pickup_tab_settings.pickup_settings.enabled_brands
              ? pickup_tab_settings.pickup_settings.enabled_brands.map(brand => {
                  return brand.branches.map(branch => branch.uuid)
                }).flat()
              : []
          },
          scheduled_pickup: {
            enable: pickup_tab_settings.pre_order_schedule_settings.is_enabled == 1 ? true : false,
            same_as_delivery: pickup_tab_settings.pre_order_schedule_settings.same_as_delivery,
            branch_uuids: pickup_tab_settings.pre_order_schedule_settings.enabled_brands
              ? pickup_tab_settings.pre_order_schedule_settings.enabled_brands.map(brand => {
                  return brand.branches.map(branch => branch.uuid)
                }).flat()
              : [],
            minimum_lead_time: pickup_tab_settings.pre_order_schedule_settings.lead_time.minimum,
            minimum: pickup_tab_settings.pre_order_schedule_settings.lead_time.minimum_by,
            duration: pickup_tab_settings.pre_order_schedule_settings.lead_time.duration_by,
            duration_lead_time: pickup_tab_settings.pre_order_schedule_settings.lead_time.duration
          }
        };
        this.pickup_now_branches = pickup_tab_settings.pickup_settings.enabled_brands
          ? pickup_tab_settings.pickup_settings.enabled_brands
          : [];
        this.scheduled_pickup_branches = pickup_tab_settings.pre_order_schedule_settings.enabled_brands
          ? pickup_tab_settings.pre_order_schedule_settings.enabled_brands
          : [];
      } else {
        this.form = initForm();
        this.pickup_now_branches = [];
        this.scheduled_pickup_branches = [];
      }
    },
    isSameDeliveryDisabled(field) {
      const fields = {
        pickup_now: 'delivery_settings',
        scheduled_pickup: 'pre_order_schedule_settings'
      };
      return this.delivery_tab_settings
        ? this.isFulfillmentNotSet || this.delivery_tab_settings[fields[field]].is_enabled === 0
        : true;
    }
  },
  watch: {
    is_altered: {
      deep: true,
      handler() {
        const is_altered = this.is_altered;
        if (is_altered.pickup_now) {
          this.form.pickup_now.same_as_delivery = false;
          this.$nextTick(() => {
            is_altered.pickup_now = false;
          });
        } else if (is_altered.scheduled_pickup) {
          this.form.scheduled_pickup.same_as_delivery = false;
          this.$nextTick(() => {
            is_altered.scheduled_pickup = false;
          });
        }
      }
    },
    isScheduledPickupDisabled(value) {
      if (value) this.form.scheduled_pickup.enable = true;
    }
  }
}
</script>

<style lang="scss" scoped>
.offset-top {
  position: relative;
  top: -3px;
}
@import url('../../../assets/scss/fulfillment/_base.scss');
</style>
